import { useContext, useMemo } from "react";
import { useIntl } from "react-intl";

import {
  BuilderContext,
  type RegisteredComponent,
} from "@builder.io/sdk-react";

import {
  type RawGoalData,
  createLevels,
} from "~/hooks/learning-center/transformers";
import { useFetchGoal } from "~/hooks/learning-center/use-fetch-lms";
import useLearningCenter from "~/hooks/use-learning-center";

import Level from "./level";
import messages from "./messages";

/**
 * SFCC-11504 — port of hec-composable `learning-center-levels/learningCenterLevels.jsx`.
 * The LMS goal dashboard block (HSA Learning Center): reads the goal reference
 * from the page entry's `BuilderContext.content.data.goal`, fetches the goal's
 * full level→module→lesson tree via `useFetchGoal`, overlays the learner's SCAPI
 * progress (`useLearningCenter`), and renders one `<Level>` carousel per level.
 *
 * `noWrap` block — registered as "Learning Center Levels" (the name the live
 * Builder content references) and must spread `attributes` on its root element.
 *
 * Data note (SFCC-11504): the goal resource route (`type=goal`) recursively
 * resolves modules + lessons server-side; a plain `useFetchGoal` enrich only
 * resolves levels, which would leave every module card blank.
 */

interface LearningCenterLevelsProps {
  attributes?: Record<string, unknown>;
}

const LearningCenterLevels = ({ attributes }: LearningCenterLevelsProps) => {
  const intl = useIntl();
  const builderContext = useContext(BuilderContext);
  const goalRef = (
    builderContext?.content?.data as { goal?: { id?: string } } | undefined
  )?.goal;
  const goalContentId = goalRef?.id;

  const { data: goalEntry, isLoading } = useFetchGoal(goalContentId);
  const { learningCenterData, getGoalByID } = useLearningCenter();

  const goalData = goalEntry?.data as RawGoalData | undefined;
  const goalBusinessId = goalData?.id ?? "";

  const levelsModel = useMemo(() => {
    if (!goalData) return null;
    // Overlay the learner's progress onto the resolved Builder tree. Deps are
    // [goalEntry, learningCenterData] (not getGoalByID): `getGoalByID` is
    // memoized on `learningCenterData` in use-learning-center, so that value is
    // its proxy — listing the fn would just churn the memo every render.
    return createLevels(goalData, getGoalByID(goalData.id) ?? undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goalEntry, learningCenterData]);

  // No goal wired on the entry (e.g. the Builder editor canvas) — mirror
  // composable's hint rather than an empty band.
  if (!goalContentId) {
    return (
      <div {...(attributes ?? {})} data-testid="lms-levels-empty">
        {intl.formatMessage(messages.selectGoal)}
      </div>
    );
  }

  // Client fetch in flight — also the SSR/first-paint state, since the fetcher
  // runs post-hydration. Reserve the band with a skeleton (4-up desktop / 1-up
  // mobile) so content swap-in doesn't shift layout.
  if (isLoading || !levelsModel) {
    return (
      <div {...(attributes ?? {})} data-testid="lms-levels-loading">
        <div className="bg-indigo-01/40">
          <div
            className="container space-y-11 py-10 lg:space-y-16 lg:py-20"
            aria-busy="true"
          >
            <div className="h-8 w-64 animate-pulse rounded bg-black/10" />
            <div className="flex gap-3">
              <div className="aspect-video w-full animate-pulse rounded-md bg-white/60 lg:hidden" />
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="aspect-video hidden flex-1 animate-pulse rounded-md bg-white/60 lg:block"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Empty-level / empty-modules guard (SFCC-11724). The goal resolved but its
  // Builder tree carries no levels (or every level has no modules) — render a
  // graceful empty band instead of a styled-but-empty section. This is the
  // render-side counterpart to the transformer-level `firstModuleUrl`/empty
  // hazard (LC-CMS-05 locks the transformer; this owns the visible state).
  const renderableLevels = levelsModel.filter(
    level => level.modules.length > 0,
  );
  if (renderableLevels.length === 0) {
    return (
      <div {...(attributes ?? {})} data-testid="lms-levels-empty-content">
        <div className="bg-indigo-01/40">
          <div className="container py-10 text-center lg:py-20">
            <p className="text-sm text-foreground">
              Lessons for this goal are coming soon.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div {...(attributes ?? {})} data-testid="learningCenterLevels">
      <div className="bg-indigo-01/40">
        <div className="container space-y-11 py-10 lg:space-y-16 lg:py-20">
          {renderableLevels.map((level, levelIndex) => (
            <Level
              key={level.levelID || levelIndex}
              levelData={level}
              goalID={goalBusinessId}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export const LearningCenterLevelsDefinition = {
  component: LearningCenterLevels,
  name: "Learning Center Levels",
  noWrap: true,
  canHaveChildren: false,
} satisfies RegisteredComponent;

export default LearningCenterLevels;
